/*
 * Copyright (c) 2017-present, flop! contributors
 * SPDX-License-Identifier: AGPL-3.0-or-later
 * https://framagit.org/flopedt/flopedt
 */

import type { AnyCalendarDate, AnyTime, CalendarDateTime, TimeDuration, ZonedDateTime } from '@internationalized/date'
import type { Timestamp } from '@quasar/quasar-ui-qcalendar'
import type { DateTimeOptions } from 'vue-i18n'

import { CalendarDate, parseDuration, Time, toCalendarDate, toLocalTimeZone, toZoned } from '@internationalized/date'
import { isBeforeOrSame } from 'reka-ui/date'

import { i18n } from '@/i18n/index.ts'
import { timeFormat } from '@/i18n/settings.ts'

export type ExtraDateTimeOptions = Omit<DateTimeOptions, 'hour12' | 'timeZone'>

/** Note: duration in minutes */
export type DstEdgeInfo = {
  at: Time
  duration: number
}

export type DatedDstEdgeInfo = DstEdgeInfo & { date: CalendarDate }

/**
 * Converts a time object into a number of whole minutes.
 * @param time The time to convert.
 * @returns The number of whole minutes.
 */
export function timeToMinutes(time: AnyTime): number {
  return time.hour * 60 + time.minute
}

/**
 * Helper to format ZonedDateTime objects.
 * @param value The date to format.
 * @param options The format options. See {@link Intl.DateTimeFormatOptions}
 * @returns The formatted date
 */
export function formatZonedDateTime(value: ZonedDateTime, options: ExtraDateTimeOptions) {
  const hour12 = timeFormat.value === 'DEFAULT' ? undefined : timeFormat.value === 'HOUR_12'
  return i18n.global.d(value.toDate(), { ...options, timeZone: value.timeZone, hour12 })
}

/**
 * Helper to format TimeDuration objects (positive only, ms ignored).
 * @param value The duration to format.
 * @returns The ISO 8601 formatted duration
 */
export function formatTimeDuration(value: TimeDuration) {
  const timeInfo = [value.hours, value.minutes, value.seconds].map((n) => (n ?? 0).toString().padStart(2, '0'))
  return `PT${timeInfo[0]}H${timeInfo[1]}M${timeInfo[2]}S`
}

/**
 * Helper to format TimeDuration objects.
 * @param value The duration to format.
 * @returns The duration as displayed on user Interface 00h00m00s
 */
export function timeDurationToString(value: TimeDuration) {
  const timeString = [value.hours, value.minutes, value.seconds].map((n) => (n ?? 0).toString().padStart(2, '0'))
  return `${timeString[0]}h${timeString[1]}m${timeString[2]}s`
}

/**
 * Creates a copy of a datetime object and sets it to midnight.
 * @param date The datetime to transform.
 * @returns The new datetime object set to midnight.
 */
export function asMidnight(date: ZonedDateTime): ZonedDateTime {
  return date.set({ hour: 0, second: 0, minute: 0, millisecond: 0 })
}

/**
 * Helper to compare TimeDuration objects (positive only, ms ignored).
 * @param td1 First duration to compare.
 * @param td2 Second duration to compare.
 * @returns The ISO 8601 formatted duration
 */
export function equalTimeDurations(td1: TimeDuration | null, td2: TimeDuration | null) {
  if (td1 === null || td2 === null) return td1 === td2
  return td1.hours === td2.hours && td1.minutes === td2.minutes && td1.seconds === td2.seconds
}

/**
/**
 * Checks whether a given datetime has its time part midnight.
 * @param date The datetime to verify.
 * @returns Whether the time part is midnight or not.
 */
export function isMidnight(date: ZonedDateTime): boolean {
  return date.hour === 0 && date.hour === date.minute && date.minute === date.second && date.second === date.millisecond
}

/**
 * Computes the difference in minutes between two dates.
 * @param a The first date.
 * @param b The second date.
 * @returns The difference in minutes. Negative if date b is after date a.
 */
export function differenceInMinutes(a: ZonedDateTime, b: ZonedDateTime) {
  const msStart = a.toDate().getTime()
  const msEnd = b.toDate().getTime()
  return Math.ceil((msStart - msEnd) / 60e3)
}

/**
 * Computes the difference in TimeDuration between two dates.
 * @param a The first date.
 * @param b The second date.
 * @returns The difference in TimeDuration. 0 if date b is after date a.
 */
export function differenceInTimeDuration(a: ZonedDateTime, b: ZonedDateTime) {
  // could not find a faster access to TimeDuration...
  const diffMin = differenceInMinutes(a, b)
  const duration = diffMin >= 0 ? `PT${Math.floor(diffMin / 60)}H${diffMin % 60}M` : 'PT0H'
  return parseDuration(duration)
}

/**
 * Computes the difference in days between two dates.
 * @param a The first date.
 * @param b The second date.
 * @returns The difference in days. Negative if date b is after date a.
 */
export function differenceInDays(a: AnyCalendarDate, b: AnyCalendarDate) {
  const date1 = toZoned(toCalendarDate(a), 'UTC')
  const date2 = toZoned(toCalendarDate(b), 'UTC')
  return differenceInMinutes(date1, date2) / 1440
}

/**
 * Computes the difference of offset between the provided date and the user's local timezone.
 * Negative values mean the date is behind the user's local time, positive values mean the date is ahead.
 * The difference is of millisecond precision.
 * @param date The date whose time offset to compare.
 * @returns The difference between the user's local timezone offset and the date's, in milliseconds.
 */
export function getOffsetDifferenceWithLocalTime(date: ZonedDateTime) {
  return date.offset - toLocalTimeZone(date).offset
}

/**
 * Returns the extra time needed to fit the DST transition, if needed.
 *
 * This method isn't simply a boolean, as DST transitions are, in fact, not always 1-hour long.
 * I'm sure you're thrilled by this information. See {@code Australia/Lord_Howe}.
 *
 * @param start Start of the interval to check, inclusive.
 * @param end End of the interval to check, inclusive.
 * @param tz The timezone to consider.
 * @returns Null if all days are 24-hours long or less, or the information about additional time needed.
 */
export function getExtraDstTime(start: AnyCalendarDate, end: AnyCalendarDate, tz: string): DatedDstEdgeInfo | null {
  const startDate = toCalendarDate(start)
  const endDate = toCalendarDate(end)

  // Shortcut to avoid having to compute the DST information
  if (toZoned(startDate, tz).offset <= toZoned(endDate.add({ days: 1 }), tz).offset) return null

  for (let date = startDate; isBeforeOrSame(date, endDate); date = date.add({ days: 1 })) {
    const edgeInfo = getDstEdgeInfo(date, tz)
    // We only care if the transition *adds* extra time, aka when we jumped *back* in time
    // You have to think like an accountant; when it's negative we add! :)
    if (edgeInfo && edgeInfo.duration < 0) return { date, at: edgeInfo.at, duration: -edgeInfo.duration }
  }

  return null
}

/**
 * Computes the information about a day's DST transition, namely when it's happening and the shift in time.
 * @param date The date to fetch the edge information of.
 * @param tz The timezone to consider.
 * @returns Information about the DST transition, or null if there is none.
 */
export function getDstEdgeInfo(date: AnyCalendarDate, tz: string): DstEdgeInfo | null {
  // Inspired from back/flop/utils/time_helpers.py:132
  const datetime = toZoned(toCalendarDate(date), tz)
  const tomorrow = datetime.add({ days: 1 })

  if (datetime.offset === tomorrow.offset) return null
  const duration = (tomorrow.offset - datetime.offset) / 60e3

  let offsetM
  let lower = 0
  let minutes = 24 * 60 + duration
  while ((offsetM = datetime.add({ minutes }).offset) === datetime.add({ minutes: minutes - 1 }).offset) {
    const half = Math.ceil((minutes - lower) / 2) + lower
    const offsetH = datetime.add({ minutes: half }).offset

    if (offsetM === offsetH) minutes = half
    else lower = half
  }

  return { at: new Time((minutes / 60) | 0, minutes % 60), duration }
}

const d = new Date()
d.setUTCMilliseconds(0)
d.setUTCSeconds(0)

/**
 * Formats a number of minutes since midnight into a human-readable time string.
 * @param time The number of minutes since midnight.
 * @returns The formatted time string in the user's locale.
 */
export function formatAsShortTime(time: number) {
  d.setUTCHours((time / 60) | 0)
  d.setUTCMinutes(time % 60)
  return i18n.global.d(d, { timeZone: 'UTC', hour: 'numeric', minute: time % 60 ? '2-digit' : undefined })
}

/**
 * Transforms a IANA timezone identifier into a short timezone name.
 * @param date The date used to extract the timezone's name. Important for DST differentiation, and changes over time.
 * @param tz The timezone to get the name of.
 * @returns The short name of the timezone, in the user's locale.
 */
export function getShortTimezoneName(date: Date, tz: string) {
  return new Intl.DateTimeFormat(i18n.global.locale.value, { timeZone: tz, timeZoneName: 'short' })
    .formatToParts(date)
    .find((part) => part.type === 'timeZoneName')!.value
}

export function getIsoDuration(start: CalendarDateTime, end: CalendarDateTime): string {
  const diff = end.subtract({
    minutes: start.minute,
    hours: start.hour,
  })
  let isoDiff = 'PT'

  if (diff.hour !== 0) isoDiff += `${diff.hour}H`
  if (diff.minute !== 0) isoDiff += `${diff.minute}M`

  return isoDiff
}

export function sameDayInBoth(timestamp: Timestamp, iso: string) {
  const date = new Date(iso)
  return (
    timestamp.year === date.getFullYear() && timestamp.day === date.getDate() && timestamp.month === date.getMonth() + 1
  )
}

export function hourFromDate(dateString: string): string {
  const date = new Date(dateString)
  return `${date.getHours()}:${date.getMinutes()}`
}

export function hourFromDateTime(date: ZonedDateTime): string {
  return `${date.hour}:${date.minute}`
}

export function returnOnlyTime(dt: ZonedDateTime): Time {
  return new Time(dt.hour, dt.minute, dt.second, dt.millisecond)
}

export function returnOnlyDate(dt: ZonedDateTime): CalendarDate {
  return new CalendarDate(dt.year, dt.month, dt.day)
}
